import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../contexts/UserAuthContext';

const EmailVerification: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail } = useUserAuth();
  
  const email = location.state?.email || '';
  const selectedPackage = location.state?.selectedPackage;
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate('/register');
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError('');

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '');
    
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('').slice(0, 6);
      setOtp(newOtp);
      setError('');
      
      // Focus the last input
      const lastInput = document.getElementById(`otp-5`);
      lastInput?.focus();
    } else if (pastedData.length > 0) {
      // Handle partial paste
      const newOtp = [...otp];
      const remainingSlots = 6 - index;
      const pasteLength = Math.min(pastedData.length, remainingSlots);
      
      for (let i = 0; i < pasteLength; i++) {
        newOtp[index + i] = pastedData[i];
      }
      
      setOtp(newOtp);
      setError('');
      
      // Focus the next empty input or the last one
      const nextIndex = Math.min(index + pasteLength, 5);
      const nextInput = document.getElementById(`otp-${nextIndex}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError(t('auth.invalidOtp'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyEmail(email, otpString);
      
      // Redirect to requests page with selected package or to requests page for new users
      if (selectedPackage) {
        navigate('/dashboard/requests', {
          state: {
            selectedPackage: selectedPackage,
            message: 'Email verified successfully! Your selected package is ready to complete.'
          }
        });
      } else {
        // New users without preselected package should go to requests page to start creating their first request
        navigate('/dashboard/requests', {
          state: {
            message: 'Email verified successfully! Ready to create your first company formation request.'
          }
        });
      }
    } catch (error: any) {
      setLoading(false);
      setError(error.message || t('auth.verificationFailed'));
      setOtp(['', '', '', '', '', '']);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError('');

    try {
      const { default: apiService } = await import('../../services/api');
      await apiService.resendVerificationCode(email, i18n.language);
      
      setCountdown(60);
      setCanResend(false);
      alert(t('auth.verificationResent'));
    } catch (error: any) {
      setError(error.message || t('auth.resendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {t('auth.verifyEmail')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.verificationCodeSent')}<br />
            <span className="font-medium text-primary-600">{email}</span>
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                {t('auth.enterVerificationCode')}
              </label>
              <div className="flex justify-between space-x-2" dir="ltr">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={(e) => handlePaste(e, index)}
                    className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      error ? 'border-red-300' : 'border-gray-300'
                    }`}
                    maxLength={1}
                    disabled={loading}
                    dir="ltr"
                  />
                ))}
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || otp.join('').length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {loading ? t('auth.verifying') : t('auth.verify')}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t('auth.didntReceiveCode')}
              </p>
              {!canResend ? (
                <p className="text-sm text-gray-500 mt-2">
                  {t('auth.resendIn')} {countdown}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
                >
                  {resendLoading ? t('auth.sending') : t('auth.resendCode')}
                </button>
              )}
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-sm font-medium text-gray-600 hover:text-gray-500"
              >
                {t('auth.backToRegister')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;